/**
 * VRF: "View in your space" link for a product's 3D models (snippets/vrf-ar-link.liquid).
 *
 * Opens the model named in data-model-id: its USDZ in Apple AR Quick Look on iPhone and iPad, its
 * GLB in Google Scene Viewer on Android. media-gallery.js keeps data-model-id on the selected
 * finish's model. Unlike Shopify's XR button, nothing here loads a third-party script, so a
 * blocked Bugsnag can no longer hide the link.
 */
if (!customElements.get('vrf-ar-link')) {
  // Scene Viewer sends the shopper back to the page with this hash when the phone can't open it.
  const UNAVAILABLE_HASH = 'vrf-ar-unavailable';

  class VrfArLink extends HTMLElement {
    static get observedAttributes() {
      return ['data-model-id'];
    }

    /**
     * Which AR viewer this device can open. Same check as the inline script in the snippet, which
     * runs at first paint; this copy covers content inserted later (quick buy, product swaps),
     * where inline scripts don't run. Keep the two in step.
     * @returns {string} 'quick-look', 'scene-viewer' or ''.
     */
    static detectMode() {
      const ua = navigator.userAgent;
      // Android first, so the iPad test below (Mac platform with touch) can't claim an Android
      // browser, as it did under Chrome's device emulation on a Mac.
      if (/Android/i.test(ua)) return /Firefox|OculusBrowser/i.test(ua) ? '' : 'scene-viewer';

      const ios = /iPad|iPhone|iPod/.test(ua)
        || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
      if (!ios) return '';

      const probe = document.createElement('a');
      const quickLook = Boolean(probe.relList && probe.relList.supports && probe.relList.supports('ar'));
      const inAppBrowser = Boolean(window.webkit && window.webkit.messageHandlers);
      if (quickLook || (inAppBrowser && /CriOS\/|EdgiOS\/|FxiOS\/|GSA\/|DuckDuckGo\//.test(ua))) {
        return 'quick-look';
      }
      return '';
    }

    /**
     * Builds the intent link that opens a GLB in Google Scene Viewer, with the parameters
     * Shopify's XR script used (file, link, title, ARCore package), plus mode=ar_preferred so a
     * phone without AR shows the 3D view instead of nothing.
     * @param {string} glbUrl - Absolute URL of the GLB file.
     * @param {string} title - Name Scene Viewer shows.
     * @returns {string}
     */
    static sceneViewerUrl(glbUrl, title) {
      const page = window.location.href.split('#')[0];
      const params = [
        `file=${encodeURIComponent(glbUrl)}`,
        'mode=ar_preferred',
        `link=${encodeURIComponent(page)}`,
        `title=${encodeURIComponent(title)}`
      ].join('&');
      const fallback = encodeURIComponent(`${page}#${UNAVAILABLE_HASH}`);
      return `intent://arvr.google.com/scene-viewer/1.0?${params}#Intent;scheme=https;`
        + `package=com.google.ar.core;action=android.intent.action.VIEW;S.browser_fallback_url=${fallback};end;`;
    }

    connectedCallback() {
      this.link = this.querySelector('a');
      const data = this.querySelector('.js-ar-models');
      try {
        this.models = data ? JSON.parse(data.textContent) : {};
      } catch (err) {
        this.models = {};
      }
      if (typeof this.dataset.arMode === 'undefined') this.dataset.arMode = VrfArLink.detectMode();

      this.hashHandler = this.hashHandler || this.handleHash.bind(this);
      window.addEventListener('hashchange', this.hashHandler);
      this.handleHash();
      this.update();
    }

    disconnectedCallback() {
      window.removeEventListener('hashchange', this.hashHandler);
    }

    attributeChangedCallback() {
      if (this.link) this.update();
    }

    /**
     * Scene Viewer couldn't open on this phone: hide the link for the rest of the visit and take
     * the marker off the address.
     */
    handleHash() {
      if (window.location.hash !== `#${UNAVAILABLE_HASH}`) return;
      VrfArLink.unavailable = true;
      window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search);
      this.update();
    }

    /**
     * Points the link at the current model for this device's viewer, or hides it.
     */
    update() {
      const model = this.models[this.dataset.modelId];
      const mode = VrfArLink.unavailable ? '' : this.dataset.arMode;
      let href = null;

      if (model && mode === 'quick-look' && model.usdz) {
        href = new URL(model.usdz, window.location.href).href;
        this.link.rel = 'ar';
      } else if (model && mode === 'scene-viewer' && model.glb) {
        href = VrfArLink.sceneViewerUrl(new URL(model.glb, window.location.href).href, this.dataset.title);
        this.link.removeAttribute('rel');
      }

      if (href) {
        this.link.href = href;
      } else {
        this.link.removeAttribute('href');
      }
      this.link.hidden = !href;
    }
  }

  customElements.define('vrf-ar-link', VrfArLink);
}
